import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useSession } from "./lib/auth";
import { useOrgStore } from "./stores/orgStore";
import { getPreservedSearchRedirectTarget } from "./lib/signup-redirects";
import { lazyWithReload } from "./lib/lazy-with-reload";

// Loading fallback component
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="text-muted-foreground">Loading...</div>
  </div>
);

const PreserveSearchRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  return (
    <Navigate
      to={getPreservedSearchRedirectTarget({ to, search: location.search })}
      replace
    />
  );
};

const LegacyPulseRedirect = () => {
  const { orgId, automationId } = useParams<{ orgId: string; automationId?: string }>();
  const target = automationId
    ? `/org/${orgId}/app/pulse/${automationId}`
    : `/org/${orgId}/app/pulse`;

  return <Navigate to={target} replace />;
};

const LegacyFlowsRedirect = () => {
  const location = useLocation();
  return <Navigate to={location.pathname.replace("/app/flows", "/app/jobs") + location.search + location.hash} replace />;
};

const LegacyAutopilotRedirect = () => {
  const location = useLocation();
  return <Navigate to={location.pathname.replace("/app/autopilot", "/app/ai-agent") + location.search + location.hash} replace />;
};

// Lazy load all page components for code splitting
const Login = lazyWithReload(() => import("./pages/Login").then(m => ({ default: m.Login })));
const Signup = lazyWithReload(() => import("./pages/Signup").then(m => ({ default: m.Signup })));
const Terms = lazyWithReload(() => import("./pages/Terms").then(m => ({ default: m.Terms })));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
// Conversations page removed - now accessed via Inbox
const Chat = lazyWithReload(() => import("./pages/Chat").then(m => ({ default: m.Chat })));
const AcceptInvite = lazyWithReload(() => import("./pages/AcceptInvite").then(m => ({ default: m.AcceptInvite })));
const VerifyEmail = lazyWithReload(() => import("./pages/VerifyEmail").then(m => ({ default: m.VerifyEmail })));
const OAuthConsent = lazyWithReload(() => import("./pages/OAuthConsent").then(m => ({ default: m.OAuthConsent })));
const ForgotPassword = lazyWithReload(() => import("./pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword").then(m => ({ default: m.ResetPassword })));
const Download = lazyWithReload(() => import("./pages/Download").then(m => ({ default: m.Download })));
const WhatsAppAutomationsGuide = lazyWithReload(() => import("./pages/WhatsAppAutomationsGuide"));
const ClaimSubscription = lazyWithReload(() => import("./pages/ClaimSubscription").then(m => ({ default: m.ClaimSubscription })));
const Templates = lazyWithReload(() => import("./pages/Templates"));
const AdminPage = lazyWithReload(() => import("./pages/admin/AdminPage"));
const Alerts = lazyWithReload(() => import("./pages/Alerts"));

// Alerts - route-based creation flow
const AlertsLayout = lazyWithReload(() => import("./pages/alerts/AlertsLayout"));
const AlertConditionStep = lazyWithReload(() => import("./pages/alerts/steps/ConditionStep"));
const AlertChannelsStep = lazyWithReload(() => import("./pages/alerts/steps/ChannelsStep"));
const AlertSourceStep = lazyWithReload(() => import("./pages/alerts/steps/SourceStep"));
const AlertConversationsStep = lazyWithReload(() => import("./pages/alerts/steps/ConversationsStep"));
const AlertLogs = lazyWithReload(() => import("./pages/alerts/AlertLogs"));
const AlertDetail = lazyWithReload(() => import("./pages/alerts/AlertDetail"));
// Summaries and Messages removed - now accessed via Inbox
// Labels page removed - now accessed via Settings page
const Contacts = lazyWithReload(() => import("./pages/Contacts"));
const Mailbox = lazyWithReload(() => import("./pages/Mailbox"));
const Leads = lazyWithReload(() => import("./pages/Leads"));
const Tasks = lazyWithReload(() => import("./pages/Tasks"));
const Calls = lazyWithReload(() => import("./pages/Calls"));
const OpportunityPipeline = lazyWithReload(() => import("./pages/sales/OpportunityPipeline"));
const CreateOpportunity = lazyWithReload(() => import("./pages/sales/CreateOpportunity"));
const OpportunityDetail = lazyWithReload(() => import("./pages/sales/OpportunityDetail"));
const Accounts = lazyWithReload(() => import("./pages/sales/Accounts"));
const Ads = lazyWithReload(() => import("./pages/sales/Ads"));
const AccountDetail = lazyWithReload(() => import("./pages/sales/AccountDetail"));
const TaskBehavior = lazyWithReload(() => import("./pages/TaskBehavior"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const WhatsAppBusinessTemplates = lazyWithReload(() => import("./pages/WhatsAppBusinessTemplates"));
const Flows = lazyWithReload(() => import("./pages/Flows"));
const AutomationsLayout = lazyWithReload(() => import("./pages/automations/AutomationsLayout"));
const AutomationsList = lazyWithReload(() => import("./pages/automations/AutomationsList"));
const AutomationDetail = lazyWithReload(() => import("./pages/automations/AutomationDetail"));
// Autopilot - route-based creation flow
const AutopilotLayout = lazyWithReload(() => import("./pages/autopilot/AutopilotLayout"));
const AutopilotList = lazyWithReload(() => import("./pages/autopilot/AutopilotList"));
const AutopilotDetail = lazyWithReload(() => import("./pages/autopilot/AutopilotDetail"));
const AutopilotInfluenceReport = lazyWithReload(() => import("./pages/autopilot/AutopilotInfluenceReport"));
// Creation + edit flow (same steps)
const AutopilotModeStep = lazyWithReload(() => import("./pages/autopilot/steps/ModeStep"));
const AutopilotRulesStep = lazyWithReload(() => import("./pages/autopilot/steps/RulesStep"));
const AutopilotKnowledgeStep = lazyWithReload(() => import("./pages/autopilot/steps/KnowledgeStep"));

const FlowBuilder = lazyWithReload(() => import("./pages/flows/FlowBuilder"));
const FlowStatus = lazyWithReload(() => import("./pages/flows/FlowStatus"));
const FlowFailures = lazyWithReload(() => import("./pages/flows/FlowFailures"));
const SheetsIntegrations = lazyWithReload(() => import("./pages/SheetsIntegrations"));
const UserDocs = lazyWithReload(() => import("./pages/UserDocs"));

// Campaigns - route-based creation flow
const CampaignsLayout = lazyWithReload(() => import("./pages/campaigns/CampaignsLayout"));
const CampaignsList = lazyWithReload(() => import("./pages/campaigns/CampaignsList"));
const SequenceBuilder = lazyWithReload(() => import("./pages/campaigns/SequenceBuilder"));
const CampaignQuickAddPage = lazyWithReload(() => import("./pages/campaigns/CampaignQuickAddDialog"));
const CampaignDetail = lazyWithReload(() => import("./pages/campaigns/CampaignDetail"));
const CampaignTypeStep = lazyWithReload(() => import("./pages/campaigns/steps/TypeStep"));
const CampaignWelcomeStep = lazyWithReload(() => import("./pages/campaigns/steps/WelcomeStep"));
const CampaignRecipientsStep = lazyWithReload(() => import("./pages/campaigns/steps/AudienceStep"));
const CampaignInstagramPostsStep = lazyWithReload(() => import("./pages/campaigns/steps/InstagramPostsStep"));
const CampaignTriggerFiltersStep = lazyWithReload(() => import("./pages/campaigns/steps/TriggerFiltersStep"));
const CampaignMessagesStep = lazyWithReload(() => import("./pages/campaigns/steps/MessagesStep"));
const CampaignTriggerStep = lazyWithReload(() => import("./pages/campaigns/steps/TriggerStep"));
const CampaignScheduleStep = lazyWithReload(() => import("./pages/campaigns/steps/ScheduleStep"));
const CampaignSimulationStep = lazyWithReload(() => import("./pages/campaigns/steps/SimulationStep"));
const CampaignPreviewStep = lazyWithReload(() => import("./pages/campaigns/steps/PreviewStep"));

// Inbox and sub-views
const ChatsView = lazyWithReload(() => import("./pages/inbox/ChatsView"));

// Components that are used less frequently - lazy load
const IntegrationsLayout = lazyWithReload(() => import("./components/IntegrationsLayout").then(m => ({ default: m.IntegrationsLayout })));
const LabelOnboarding = lazyWithReload(() => import("./components/LabelOnboarding").then(m => ({ default: m.LabelOnboarding })));

// Onboarding steps - lazy load since only used during onboarding
const NameStep = lazyWithReload(() => import("./pages/onboarding/NameStep").then(m => ({ default: m.NameStep })));
const WhatsAppConnectStep = lazyWithReload(() => import("./pages/onboarding/WhatsAppConnectStep").then(m => ({ default: m.WhatsAppConnectStep })));
const PlanSelectionStep = lazyWithReload(() => import("./pages/onboarding/PlanSelectionStep").then(m => ({ default: m.PlanSelectionStep })));

// These are kept as regular imports since they're always needed
import { ProtectedRoute } from "./components/ProtectedRoute";
import { CompletedOnboardingRoute } from "./components/CompletedOnboardingRoute";
import { AppLayout } from "./components/AppLayout";
import SalesLayout from "./pages/sales/SalesLayout";
import { SearchChatModal } from "./components/SearchChatModal";
import { MessageDetailModal } from "./components/MessageDetailModal";

// Smart redirect for root route - redirects to dashboard if logged in, login if not
const RootRedirect = () => {
  const { data: session, isPending } = useSession();
  const { currentOrgId } = useOrgStore();

  if (isPending) {
    return <PageLoader />;
  }

  if (session?.user) {
    if (session.user.emailVerified !== true) {
      return <Navigate to="/login?verify_email=1" replace />;
    }

    // User is logged in - redirect to dashboard
    if (currentOrgId) {
      return <Navigate to={`/org/${currentOrgId}/app/dashboard`} replace />;
    }
    // No org selected yet - redirect to login so they can see org selector
    // The login page will fetch orgs and handle onboarding status check
    return <Navigate to="/login" replace />;
  }

  // Not logged in - go to login
  return <Navigate to="/login" replace />;
};

export const AppRouter = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/download" element={<Download />} />
          <Route path="/querygen-whatsapp-automations" element={<WhatsAppAutomationsGuide />} />
          <Route path="/docs" element={<UserDocs />} />
          <Route path="/docs/:section" element={<UserDocs />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          {/* Admin Route - separate authentication */}
          <Route path="/admin/*" element={<AdminPage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            {/* Claim subscription - for existing users who paid before signing in */}
            <Route path="/claim-subscription" element={<ClaimSubscription />} />

            {/* Redirect old onboarding route to new flow */}
            <Route path="/onboarding" element={<PreserveSearchRedirect to="/onboarding/name" />} />

            {/* New onboarding flow steps */}
            <Route path="/onboarding/name" element={<NameStep />} />
            <Route path="/onboarding/organization" element={<PreserveSearchRedirect to="/onboarding/name" />} />
            <Route path="/onboarding/channel" element={<WhatsAppConnectStep />} />
            <Route path="/onboarding/whatsapp" element={<PreserveSearchRedirect to="/onboarding/channel" />} />
            <Route path="/onboarding/phone" element={<Navigate to="/onboarding/channel" replace />} />
            <Route path="/onboarding/labels" element={<Navigate to="/onboarding/channel" replace />} />
            <Route path="/onboarding/team" element={<Navigate to="/onboarding/channel" replace />} />
            <Route path="/onboarding/download" element={<Navigate to="/onboarding/channel" replace />} />
            <Route path="/onboarding/complete" element={<Navigate to="/onboarding/channel" replace />} />
            <Route path="/onboarding/plan" element={<PlanSelectionStep />} />

            {/* Label onboarding - outside of AppLayout to have full screen */}
            <Route path="/org/:orgId/label-onboarding" element={<LabelOnboarding />} />
            <Route path="/org/:orgId/app/docs" element={<UserDocs />} />

            {/* Protected routes with sidebar */}
            <Route
              path="/org/:orgId/app"
              element={
                <CompletedOnboardingRoute>
                  <AppLayout />
                </CompletedOnboardingRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="dashboard/conversations" element={<Navigate to="../inbox" replace />} />

              <Route path="inbox" element={<Mailbox />}>
                <Route index element={<ChatsView />} />
                <Route path=":conversationId" element={<ChatsView />} />
              </Route>

              <Route path="mailbox" element={<Mailbox />}>
                <Route index element={<ChatsView />} />
                <Route path=":conversationId" element={<ChatsView />} />
              </Route>

              <Route path="calls" element={<Calls />} />

              {/* Que - standalone under Analytics */}
              <Route path="chat" element={<Chat />} />
              <Route path="chat/:conversationId" element={<Chat />} />

              {/* Alerts page with tabs (Alerts, Reminders, Notifications) */}
              <Route path="alerts" element={<AlertsLayout />}>
                <Route index element={<Alerts />} />
                <Route path="logs" element={<AlertLogs />} />
                <Route path="details/:alertType/:alertId" element={<AlertDetail />} />
                <Route path="new" element={<AlertConditionStep />} />
                <Route path="new/source" element={<AlertSourceStep />} />
                <Route path="new/channels" element={<AlertChannelsStep />} />
                <Route path="new/conversations" element={<AlertConversationsStep />} />
              </Route>

              {/* Backwards compatibility redirects */}
              <Route path="messages" element={<Navigate to="../inbox" replace />} />
              <Route path="messages/:conversationId" element={<Navigate to="../inbox" replace />} />
              <Route path="summaries" element={<Navigate to="../inbox" replace />} />
              <Route path="summaries/:conversationId" element={<Navigate to="../inbox" replace />} />
              <Route path="inbox/chats" element={<Navigate to="../inbox" replace />} />
              <Route path="inbox/chats/:conversationId" element={<Navigate to="../inbox" replace />} />
              <Route path="daily-brief" element={<Navigate to="../dashboard" replace />} />
              <Route path="inbox/daily-brief" element={<Navigate to="../dashboard" replace />} />
              <Route path="alert-history" element={<Navigate to="../alerts?tab=notifications" replace />} />
              <Route path="notifications" element={<Navigate to="../alerts?tab=notifications" replace />} />
              <Route path="reminders" element={<Navigate to="../alerts?tab=reminders" replace />} />
              <Route path="templates" element={<Templates />} />
              <Route path="templates/:templateId" element={<Templates />} />
              <Route path="sales" element={<SalesLayout />}>
                <Route index element={<Navigate to="leads" replace />} />
                <Route path="overview" element={<Navigate to="../leads" replace />} />
                <Route path="leads" element={<Leads />} />
                <Route path="pipeline" element={<OpportunityPipeline />} />
                <Route path="pipeline/new" element={<CreateOpportunity />} />
                <Route path="pipeline/:opportunityId" element={<OpportunityDetail />} />
                <Route path="opportunities" element={<PreserveSearchRedirect to="../pipeline" />} />
                <Route path="customers" element={<PreserveSearchRedirect to="../../contacts" />} />
                <Route path="accounts" element={<Accounts />} />
                <Route path="accounts/:accountId" element={<AccountDetail />} />
                <Route path="ads" element={<Ads />} />
                <Route path="tasks" element={<PreserveSearchRedirect to="../../tasks" />} />
              </Route>
              <Route path="leads" element={<PreserveSearchRedirect to="../sales/leads" />} />
              <Route path="leads/tracker/new/:step" element={<FlowBuilder mode="create" surface="leads" />} />
              <Route path="leads/tracker/:flowId/edit/:step" element={<FlowBuilder mode="edit" surface="leads" />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tasks/settings" element={<TaskBehavior />} />
              <Route path="tasks/behavior" element={<Navigate to="../tasks/settings" replace />} />
              <Route path="tasks/tracker/new/:step" element={<FlowBuilder mode="create" surface="tasks" />} />
              <Route path="tasks/tracker/:flowId/edit/:step" element={<FlowBuilder mode="edit" surface="tasks" />} />
              {/* Labels moved to Settings - redirect for backwards compatibility */}
              <Route path="labels" element={<Navigate to="../settings/labels" replace />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/whatsapp-business/templates" element={<WhatsAppBusinessTemplates />} />
              <Route path="settings/:section/:knowledgeBaseId/:knowledgeView?" element={<Settings />} />
              <Route path="settings/:section" element={<Settings />} />
              <Route path="billing" element={<Navigate to="../settings/billing" replace />} />
              <Route path="pulse" element={<AutomationsLayout />}>
                <Route index element={<AutomationsList />} />
                <Route path="new/*" element={<Navigate to=".." replace />} />
                <Route path=":automationId" element={<AutomationDetail />} />
              </Route>
              <Route path="welcome" element={<Navigate to="../campaigns/new/welcome" replace />} />
              <Route path="automations" element={<LegacyPulseRedirect />} />
              <Route path="automations/new/*" element={<LegacyPulseRedirect />} />
              <Route path="automations/:automationId" element={<LegacyPulseRedirect />} />
              {/* Jobs (internally "flows") - new automation system */}
              <Route path="jobs" element={<Flows />} />
              <Route path="jobs/failures" element={<FlowFailures />} />
              <Route path="jobs/new/:step" element={<FlowBuilder mode="create" />} />
              <Route path="jobs/:flowId/edit/:step" element={<FlowBuilder mode="edit" />} />
              <Route path="jobs/:flowId/status" element={<FlowStatus />} />
              <Route path="flows/*" element={<LegacyFlowsRedirect />} />
              {/* AI Agent (internally "autopilot") - route-based creation flow */}
              <Route path="ai-agent" element={<AutopilotLayout />}>
                <Route index element={<AutopilotList />} />
                <Route path="new" element={<Navigate to="mode" replace />} />
                <Route path="new/mode" element={<AutopilotModeStep />} />
                <Route path="new/rules" element={<AutopilotRulesStep />} />
                <Route path="new/instructions" element={<AutopilotKnowledgeStep />} />
                <Route path="new/knowledge" element={<AutopilotKnowledgeStep />} />
                <Route path="new/channels" element={<AutopilotKnowledgeStep />} />
                <Route path="new/web-channel" element={<AutopilotKnowledgeStep />} />
                <Route path="new/conversations" element={<AutopilotKnowledgeStep />} />
                <Route path="influence" element={<AutopilotInfluenceReport />} />
                <Route path=":configId" element={<AutopilotDetail />} />
                <Route path=":configId/edit/rules" element={<AutopilotRulesStep />} />
                <Route path=":configId/edit/instructions" element={<AutopilotKnowledgeStep />} />
                <Route path=":configId/edit/knowledge" element={<AutopilotKnowledgeStep />} />
                <Route path=":configId/edit/channels" element={<AutopilotKnowledgeStep />} />
                <Route path=":configId/edit/web-channel" element={<AutopilotKnowledgeStep />} />
                <Route path=":configId/edit/conversations" element={<AutopilotKnowledgeStep />} />
              </Route>
              <Route path="autopilot/*" element={<LegacyAutopilotRedirect />} />
              {/* Campaigns - route-based creation flow */}
              <Route path="campaigns" element={<CampaignsLayout />}>
                <Route index element={<CampaignsList />} />
                <Route path="sequences/new" element={<SequenceBuilder />} />
                <Route path="sequences/:sequenceId" element={<SequenceBuilder />} />
                <Route path="welcome" element={<Navigate to="new/welcome" replace />} />
                <Route path="quick-add" element={<CampaignQuickAddPage />} />
                <Route path=":campaignId" element={<CampaignDetail />} />
                <Route path="new" element={<CampaignTypeStep />} />
                <Route path="new/welcome" element={<CampaignWelcomeStep />} />
                <Route path="new/recipients" element={<CampaignRecipientsStep />} />
                <Route path="new/posts" element={<CampaignInstagramPostsStep />} />
                <Route path="new/filters" element={<CampaignTriggerFiltersStep />} />
                <Route path="new/messages" element={<CampaignMessagesStep />} />
                <Route path="new/trigger" element={<CampaignTriggerStep />} />
                <Route path="new/schedule" element={<CampaignScheduleStep />} />
                <Route path="new/simulation" element={<CampaignSimulationStep />} />
                <Route path="new/preview" element={<CampaignPreviewStep />} />
              </Route>
              {/* Legacy Integrations routes - redirect to flows */}
              <Route path="integrations" element={<IntegrationsLayout />}>
                <Route index element={<Navigate to="google-sheets" replace />} />
                <Route path="google-sheets" element={<SheetsIntegrations />} />
              </Route>
            </Route>
          </Route>

          {/* Default redirect - smart redirect based on auth state */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </Suspense>

      {/* Global modals - need to be inside BrowserRouter for useNavigate */}
      <SearchChatModal />
      <MessageDetailModal />
    </BrowserRouter>
  );
};
